/**
 * ClientSelectionFlow Component
 * Selection request workflow for clients
 * Mobile-first with selection counter, review screen, and submit
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCheck, FiX, FiArrowLeft, FiSend, FiInfo, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { usePortalSession } from './ClientPortal';

const API_BASE = '/api/portal';

// Selection indicator overlay
function SelectionOverlay({ isSelected, number }) {
  if (!isSelected) return null;

  return (
    <div className="absolute inset-0 bg-blue-500/20 border-4 border-blue-500 rounded-lg flex items-center justify-center">
      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
        {number}
      </div>
    </div>
  );
}

// Selectable image card
function SelectableImage({ image, isSelected, selectionNumber, onToggle, imageUrl }) {
  return (
    <div
      className={`relative cursor-pointer overflow-hidden rounded-lg bg-gray-100 transition-all ${isSelected ? 'ring-4 ring-blue-500' : ''}`}
      style={{ aspectRatio: image.width && image.height ? `${image.width}/${image.height}` : '4/3' }}
      onClick={() => onToggle(image.id)}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={image.title || 'Photo'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      <SelectionOverlay isSelected={isSelected} number={selectionNumber} />

      {/* Selection checkbox - always visible */}
      <div className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-colors ${isSelected ? 'bg-blue-500 text-white' : 'bg-white text-gray-400'}`}>
        {isSelected ? <FiCheck size={18} /> : <span className="text-xs">{selectionNumber || ''}</span>}
      </div>
    </div>
  );
}

// Selection counter bar
function SelectionCounter({ count, min, max, onReview }) {
  const isValid = (!min || count >= min) && (!max || count <= max);
  const canSubmit = count > 0 && isValid;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg safe-area-inset-bottom">
      <div className="px-4 py-3 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-gray-900">{count}</span>
            <span className="text-gray-500">selected</span>
          </div>
          {(min || max) && (
            <p className={`text-sm ${isValid ? 'text-gray-500' : 'text-red-500'}`}>
              {min && max ? `${min}-${max} required` : min ? `Min ${min}` : max ? `Max ${max}` : ''}
            </p>
          )}
        </div>
        <button
          onClick={onReview}
          disabled={!canSubmit}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          Review
          <FiArrowLeft size={18} className="rotate-180" />
        </button>
      </div>
    </div>
  );
}

// Review screen
function ReviewScreen({ selections, images, imageUrls, onBack, onSubmit, submitting }) {
  const selectedImages = useMemo(() =>
    selections.map(id => images.find(img => img.id === id)).filter(Boolean),
    [selections, images]
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
            <FiArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-gray-900">Review Selection</h1>
            <p className="text-sm text-gray-500">{selections.length} photos selected</p>
          </div>
        </div>
      </header>

      {/* Selected images */}
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {selectedImages.map((image, index) => (
            <div key={image.id} className="relative rounded-lg overflow-hidden bg-gray-100" style={{ aspectRatio: '4/3' }}>
              {imageUrls[image.id] && (
                <img
                  src={imageUrls[image.id]}
                  alt={image.title || 'Selected photo'}
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute top-2 left-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Submit bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg safe-area-inset-bottom">
        <div className="px-4 py-3 flex items-center justify-between gap-4">
          <button
            onClick={onBack}
            className="px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg font-medium"
          >
            Back to Gallery
          </button>
          <button
            onClick={onSubmit}
            disabled={submitting}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? 'Submitting...' : 'Submit Selection'}
            <FiSend size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Success screen
function SuccessScreen({ count, onDone }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <FiCheck size={40} className="text-green-500" />
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">Selection Submitted!</h1>
        <p className="mt-3 text-gray-500">
          You've selected {count} photos. The photographer will review your selection.
        </p>
        <button
          onClick={onDone}
          className="mt-8 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// Main selection flow
export default function ClientSelectionFlow() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { headers } = usePortalSession();

  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [selections, setSelections] = useState([]);
  const [showReview, setShowReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadSelectionRequest();
  }, []);

  const loadSelectionRequest = async () => {
    try {
      const res = await fetch(`${API_BASE}/selection`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRequest(data.request);
        setImages(data.images);

        // Load existing selections if any
        if (data.latest_submission?.selections) {
          setSelections(data.latest_submission.selections.map(s => s.assetId || s));
        }

        // Load image thumbnails
        loadImageUrls(data.images);
      }
    } catch (err) {
      console.error('Failed to load selection request:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadImageUrls = async (imgs) => {
    const urls = {};
    await Promise.all(imgs.map(async (img) => {
      try {
        const res = await fetch(`${API_BASE}/image/${img.id}/sm`, { headers });
        if (res.ok) {
          const data = await res.json();
          urls[img.id] = data.url;
        }
      } catch {}
    }));
    setImageUrls(urls);
  };

  const toggleSelection = (imageId) => {
    const rules = request?.rules || {};

    setSelections(prev => {
      if (prev.includes(imageId)) {
        // Remove
        return prev.filter(id => id !== imageId);
      } else {
        // Add (check max)
        if (rules.maxSelections && prev.length >= rules.maxSelections) {
          return prev;
        }
        return [...prev, imageId];
      }
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const selectionsData = selections.map((id, index) => ({
        assetId: id,
        rank: index + 1
      }));

      const res = await fetch(`${API_BASE}/selection/submit`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ selections: selectionsData })
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json();
        alert(data.message || 'Failed to submit selection');
      }
    } catch (err) {
      console.error('Failed to submit:', err);
      alert('Failed to submit selection');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Selection request not found</p>
      </div>
    );
  }

  if (submitted) {
    return <SuccessScreen count={selections.length} onDone={() => navigate(`/c/${token}`)} />;
  }

  if (showReview) {
    return (
      <ReviewScreen
        selections={selections}
        images={images}
        imageUrls={imageUrls}
        onBack={() => setShowReview(false)}
        onSubmit={handleSubmit}
        submitting={submitting}
      />
    );
  }

  const rules = request.rules || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => navigate(`/c/${token}`)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <FiArrowLeft size={20} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="font-semibold text-gray-900 truncate">{request.title}</h1>
            <p className="text-sm text-gray-500">Tap photos to select</p>
          </div>
        </div>

        {/* Instructions */}
        {request.instructions && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
            <div className="flex items-start gap-2">
              <FiInfo size={16} className="text-blue-500 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-700">{request.instructions}</p>
            </div>
          </div>
        )}
      </header>

      {/* Image grid */}
      <div className="p-2 sm:p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5 sm:gap-2">
          {images.map(image => {
            const selectionIndex = selections.indexOf(image.id);
            const isSelected = selectionIndex !== -1;

            return (
              <SelectableImage
                key={image.id}
                image={image}
                isSelected={isSelected}
                selectionNumber={isSelected ? selectionIndex + 1 : null}
                onToggle={toggleSelection}
                imageUrl={imageUrls[image.id]}
              />
            );
          })}
        </div>
      </div>

      {/* Selection counter */}
      <SelectionCounter
        count={selections.length}
        min={rules.minSelections}
        max={rules.maxSelections}
        onReview={() => setShowReview(true)}
      />
    </div>
  );
}
